import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import { Translate } from '../../shared/LanguageService';
import { useFeatureFlagOptionsQuery } from './queries/useFeatureFlagOptionsQuery';
import { RuleError, RuleErrors } from './ruleValidation';
import {
    BrokerOption,
    CountryOption,
    FeatureFlagRuleInput,
    FeatureFlagRuleOperator,
    FeatureFlagRuleType,
    RULE_OPERATORS,
    RULE_TYPES
} from './types';

interface RuleEditorProps {
    rules: FeatureFlagRuleInput[];
    variants?: string[];
    onChange: (rules: FeatureFlagRuleInput[]) => void;
    // Populated once a save has been attempted; index-aligned with rules.
    errors?: RuleErrors;
}

function priorityErrorText(error: RuleError | null): string | null {
    if (error === null || error.priority === undefined) {
        return null;
    }
    return error.priority === 'duplicate'
        ? Translate('feature-flags/rules/errors/priority-duplicate')
        : Translate('feature-flags/rules/errors/priority-positive');
}

function valuesErrorText(error: RuleError | null): string | null {
    if (error === null || error.values === undefined) {
        return null;
    }
    return Translate('feature-flags/rules/errors/values-required');
}

function makeEmptyRule(priority: number, variants: string[]): FeatureFlagRuleInput {
    return {
        id: null,
        priority,
        ruleType: RULE_TYPES[0],
        operator: RULE_OPERATORS[0],
        values: [],
        resultEnabled: true,
        resultVariant: variants.length > 0 ? variants[0] : null
    };
}

function normaliseValues(rawValues: (string | null | undefined)[]): string[] {
    const flattened: string[] = [];
    for (const value of rawValues) {
        if (typeof value !== 'string') {
            continue;
        }
        if (value.indexOf(',') !== -1) {
            const parts = value
                .split(',')
                .map((part) => part.trim())
                .filter((part) => part.length > 0);
            for (const part of parts) {
                flattened.push(part);
            }
        } else {
            const trimmed = value.trim();
            if (trimmed.length > 0) {
                flattened.push(trimmed);
            }
        }
    }
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const value of flattened) {
        const lower = value.toLowerCase();
        if (!seen.has(lower)) {
            seen.add(lower);
            unique.push(value);
        }
    }
    return unique;
}

export default function RuleEditor({
    rules,
    variants = [],
    onChange,
    errors
}: RuleEditorProps) {
    const { data: options } = useFeatureFlagOptionsQuery();
    const roleOptions = options ? options.roles : [];
    const countryOptions = options ? options.countries : [];
    const brokerFirms = options ? options.brokerFirms : [];
    const brokerChains = options ? options.brokerChains : [];

    const [pendingDeleteIndex, setPendingDeleteIndex] = React.useState<number | null>(null);

    // Undefined until the first save attempt, and index-aligned with rules once present.
    const errorAt = (index: number): RuleError | null =>
        errors !== undefined && index < errors.length ? errors[index] : null;

    const updateRule = (index: number, patch: Partial<FeatureFlagRuleInput>) => {
        const next = rules.slice();
        next[index] = { ...next[index], ...patch };
        onChange(next);
    };

    const requestRemove = (index: number) => {
        setPendingDeleteIndex(index);
    };

    const cancelRemove = () => {
        setPendingDeleteIndex(null);
    };

    const confirmRemove = () => {
        if (pendingDeleteIndex === null) {
            return;
        }
        const next = rules.slice();
        next.splice(pendingDeleteIndex, 1);
        setPendingDeleteIndex(null);
        onChange(next);
    };

    const addRule = () => {
        const nextPriority =
            rules.length === 0 ? 1 : Math.max.apply(null, rules.map((r) => r.priority)) + 1;
        onChange([...rules, makeEmptyRule(nextPriority, variants)]);
    };

    const pendingDeleteRule =
        pendingDeleteIndex !== null && pendingDeleteIndex < rules.length
            ? rules[pendingDeleteIndex]
            : null;

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">{Translate('feature-flags/rules/title')}</Typography>
                <Button onClick={addRule} variant="outlined" size="small">
                    {Translate('feature-flags/rules/add-button')}
                </Button>
            </Stack>
            {rules.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    {Translate('feature-flags/rules/empty')}
                </Typography>
            )}
            <Stack spacing={2}>
                {rules.map((rule, index) => (
                    <Paper
                        // Index is stable across the form session because we only push/splice;
                        // if ordering becomes draggable, switch to a synthetic client id.
                        key={index}
                        variant="outlined"
                        sx={{ p: 2 }}
                    >
                        <Stack spacing={2}>
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                            >
                                <TextField
                                    label={Translate('feature-flags/rules/priority-label')}
                                    type="number"
                                    size="small"
                                    value={rule.priority}
                                    onChange={(e) => {
                                        // An empty field parses to NaN here; keep 0 so the input
                                        // stays controlled, and let validation flag it.
                                        const parsed = Number(e.target.value);
                                        updateRule(index, {
                                            priority: isNaN(parsed) ? 0 : parsed
                                        });
                                    }}
                                    error={priorityErrorText(errorAt(index)) !== null}
                                    helperText={priorityErrorText(errorAt(index))}
                                    sx={{ width: 90 }}
                                />
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <InputLabel>{Translate('feature-flags/rules/type-label')}</InputLabel>
                                    <Select
                                        label={Translate('feature-flags/rules/type-label')}
                                        value={rule.ruleType}
                                        onChange={(e) => {
                                            const nextType =
                                                e.target.value as FeatureFlagRuleType;
                                            if (nextType === rule.ruleType) {
                                                return;
                                            }
                                            // Values are domain-specific (emails vs roles vs
                                            // ids); reset when the type changes so we don't
                                            // carry stale entries into the new type's input.
                                            updateRule(index, {
                                                ruleType: nextType,
                                                values: []
                                            });
                                        }}
                                    >
                                        {RULE_TYPES.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ width: 110 }}>
                                    <InputLabel>{Translate('feature-flags/rules/operator-label')}</InputLabel>
                                    <Select
                                        label={Translate('feature-flags/rules/operator-label')}
                                        value={rule.operator}
                                        onChange={(e) =>
                                            updateRule(index, {
                                                operator:
                                                    e.target.value as FeatureFlagRuleOperator
                                            })
                                        }
                                    >
                                        {RULE_OPERATORS.map((op) => (
                                            <MenuItem key={op} value={op}>
                                                {op}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{ flexGrow: 1 }} />
                                {variants.length > 0 ? (
                                    <FormControl size="small" sx={{ width: 170 }}>
                                        <InputLabel id={`feature-flag-rule-${index}-variant-label`}>
                                            {Translate('feature-flags/common/variant-label')}
                                        </InputLabel>
                                        <Select
                                            labelId={`feature-flag-rule-${index}-variant-label`}
                                            label={Translate('feature-flags/common/variant-label')}
                                            value={
                                                rule.resultVariant !== null ? rule.resultVariant : ''
                                            }
                                            onChange={(e) =>
                                                updateRule(index, {
                                                    resultVariant: e.target.value as string
                                                })
                                            }
                                        >
                                            {variants.map((variant) => (
                                                <MenuItem key={variant} value={variant}>
                                                    {variant}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <Chip
                                        clickable
                                        color={rule.resultEnabled ? 'success' : 'default'}
                                        label={
                                            rule.resultEnabled
                                                ? Translate('feature-flags/common/enabled')
                                                : Translate('feature-flags/common/disabled')
                                        }
                                        onClick={() =>
                                            updateRule(index, {
                                                resultEnabled: !rule.resultEnabled
                                            })
                                        }
                                        sx={{ minWidth: 110 }}
                                    />
                                )}
                                <IconButton
                                    aria-label={Translate('feature-flags/rules/remove-rule-aria')}
                                    onClick={() => requestRemove(index)}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                            <ValuesField
                                ruleIndex={index}
                                rule={rule}
                                roleOptions={roleOptions}
                                countryOptions={countryOptions}
                                brokerFirms={brokerFirms}
                                brokerChains={brokerChains}
                                errorText={valuesErrorText(errorAt(index))}
                                onValuesChange={(values) => updateRule(index, { values })}
                            />
                            {/* "NotIn" reads as "everyone except these", but the backend only
                                applies a rule to users who actually have the attribute it targets
                                — a non-broker has no BrokerFirm, an image editor no Country. Say so
                                here, where the rule is written, rather than let authors discover it
                                from a flag that didn't reach the users they expected. */}
                            {rule.operator === 'NotIn' && (
                                <Typography variant="caption" color="text.secondary">
                                    {Translate('feature-flags/rules/notin-helper', {
                                        ruleType: rule.ruleType
                                    })}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                ))}
            </Stack>
            <Dialog
                open={pendingDeleteIndex !== null}
                onClose={cancelRemove}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{Translate('feature-flags/rules/remove-dialog/title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingDeleteRule !== null
                            ? Translate('feature-flags/rules/remove-dialog/confirm', {
                                  ruleType: pendingDeleteRule.ruleType,
                                  operator: pendingDeleteRule.operator,
                                  priority: pendingDeleteRule.priority
                              })
                            : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelRemove}>{Translate('feature-flags/common/cancel')}</Button>
                    <Button onClick={confirmRemove} color="error" variant="contained">
                        {Translate('feature-flags/rules/remove-dialog/remove-button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

interface ValuesFieldProps {
    ruleIndex: number;
    rule: FeatureFlagRuleInput;
    roleOptions: string[];
    countryOptions: CountryOption[];
    brokerFirms: BrokerOption[];
    brokerChains: BrokerOption[];
    errorText: string | null;
    onValuesChange: (values: string[]) => void;
}

function ValuesField({
    ruleIndex,
    rule,
    roleOptions,
    countryOptions,
    brokerFirms,
    brokerChains,
    errorText,
    onValuesChange
}: ValuesFieldProps) {
    if (rule.ruleType === 'BrokerFirm' || rule.ruleType === 'BrokerChain') {
        return (
            <BrokerOptionAutocomplete
                ruleIndex={ruleIndex}
                rule={rule}
                options={rule.ruleType === 'BrokerFirm' ? brokerFirms : brokerChains}
                errorText={errorText}
                onValuesChange={onValuesChange}
            />
        );
    }
    if (rule.ruleType === 'Country') {
        return (
            <CountryAutocomplete
                ruleIndex={ruleIndex}
                rule={rule}
                options={countryOptions}
                errorText={errorText}
                onValuesChange={onValuesChange}
            />
        );
    }
    return (
        <StringAutocomplete
            ruleIndex={ruleIndex}
            rule={rule}
            options={rule.ruleType === 'Role' ? roleOptions : []}
            errorText={errorText}
            onValuesChange={onValuesChange}
        />
    );
}

interface StringAutocompleteProps {
    ruleIndex: number;
    rule: FeatureFlagRuleInput;
    options: string[];
    errorText: string | null;
    onValuesChange: (values: string[]) => void;
}

function StringAutocomplete({
    ruleIndex,
    rule,
    options,
    errorText,
    onValuesChange
}: StringAutocompleteProps) {
    const isRole = rule.ruleType === 'Role';

    let label = Translate('feature-flags/rules/values-label');
    let placeholder = '';
    let helperText = '';
    if (rule.ruleType === 'Email') {
        label = Translate('feature-flags/rules/emails-label');
        placeholder = Translate('feature-flags/rules/emails-placeholder');
        helperText = Translate('feature-flags/rules/emails-helper');
    } else if (isRole) {
        label = Translate('feature-flags/rules/roles-label');
        placeholder = Translate('feature-flags/rules/roles-placeholder');
        helperText = Translate('feature-flags/rules/roles-helper');
    }

    // For strict (non-freeSolo) modes we include any pre-existing values that aren't in the
    // backend list, so MUI doesn't warn about "value not in options" and the admin can still
    // remove orphans.
    const enrichedOptions = React.useMemo(() => {
        if (!isRole) {
            return options;
        }
        const seen = new Set(options.map((o) => o.toLowerCase()));
        const extras = rule.values.filter((v) => !seen.has(v.toLowerCase()));
        return extras.length === 0 ? options : options.concat(extras);
    }, [isRole, options, rule.values]);

    return (
        <Autocomplete
            multiple
            freeSolo={!isRole}
            autoSelect={!isRole}
            size="small"
            fullWidth
            options={enrichedOptions}
            value={rule.values}
            onChange={(_event, newValues) => onValuesChange(normaliseValues(newValues))}
            renderTags={(value, getTagProps) =>
                value.map((option, tagIndex) => {
                    const tagProps = getTagProps({ index: tagIndex });
                    return (
                        <Chip
                            {...tagProps}
                            key={`${ruleIndex}-${tagIndex}`}
                            label={option}
                            size="small"
                            sx={{ zIndex: 1500 }}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={rule.values.length === 0 ? placeholder : undefined}
                    error={errorText !== null}
                    helperText={errorText !== null ? errorText : helperText}
                />
            )}
        />
    );
}

interface CountryAutocompleteProps {
    ruleIndex: number;
    rule: FeatureFlagRuleInput;
    options: CountryOption[];
    errorText: string | null;
    onValuesChange: (values: string[]) => void;
}

function CountryAutocomplete({
    ruleIndex,
    rule,
    options,
    errorText,
    onValuesChange
}: CountryAutocompleteProps) {
    // Rule values are country codes (e.g. "se"), matched against the principal's country on the
    // backend. Names are translation keys, so labels are localized for display.
    const labelFor = (option: CountryOption) =>
        option.name ? Translate(option.name) : option.code;

    const valueObjects: CountryOption[] = React.useMemo(() => {
        return rule.values.map((code) => {
            const found = options.find((o) => o.code === code);
            return found ? found : { code, name: '' };
        });
    }, [rule.values, options]);

    return (
        <Autocomplete
            multiple
            size="small"
            fullWidth
            options={options}
            value={valueObjects}
            getOptionLabel={labelFor}
            isOptionEqualToValue={(a: CountryOption, b: CountryOption) => a.code === b.code}
            onChange={(_event, newOptions: CountryOption[]) => {
                onValuesChange(newOptions.map((o) => o.code));
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, tagIndex) => {
                    const tagProps = getTagProps({ index: tagIndex });
                    return (
                        <Chip
                            {...tagProps}
                            key={`${ruleIndex}-${tagIndex}`}
                            label={labelFor(option)}
                            size="small"
                            sx={{ zIndex: 1500 }}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={Translate('feature-flags/rules/countries-label')}
                    placeholder={
                        rule.values.length === 0
                            ? Translate('feature-flags/rules/countries-placeholder')
                            : undefined
                    }
                    error={errorText !== null}
                    helperText={errorText}
                />
            )}
        />
    );
}

interface BrokerAutocompleteProps {
    ruleIndex: number;
    rule: FeatureFlagRuleInput;
    options: BrokerOption[];
    errorText: string | null;
    onValuesChange: (values: string[]) => void;
}

function BrokerOptionAutocomplete({
    ruleIndex,
    rule,
    options,
    errorText,
    onValuesChange
}: BrokerAutocompleteProps) {
    const isFirm = rule.ruleType === 'BrokerFirm';
    const label = isFirm
        ? Translate('feature-flags/rules/broker-firms-label')
        : Translate('feature-flags/rules/broker-chains-label');
    const placeholder = isFirm
        ? Translate('feature-flags/rules/broker-firms-placeholder')
        : Translate('feature-flags/rules/broker-chains-placeholder');

    // Map the rule's string-id values into BrokerOption objects for the Autocomplete. If the
    // backend list doesn't include an id (firm was removed or not yet loaded), synthesise a
    // placeholder so the chip still renders and admin can remove it.
    const valueObjects: BrokerOption[] = React.useMemo(() => {
        return rule.values.map((rawId) => {
            const id = parseInt(rawId, 10);
            const found = options.find((o) => o.id === id);
            if (found) {
                return found;
            }
            return {
                id: isNaN(id) ? -1 : id,
                name: Translate('feature-flags/rules/broker-unknown', { brokerId: rawId })
            };
        });
    }, [rule.values, options]);

    return (
        <Autocomplete
            multiple
            size="small"
            fullWidth
            options={options}
            value={valueObjects}
            getOptionLabel={(option: BrokerOption) => option.name}
            isOptionEqualToValue={(a: BrokerOption, b: BrokerOption) => a.id === b.id}
            onChange={(_event, newOptions: BrokerOption[]) => {
                const ids = newOptions.map((o) => String(o.id));
                onValuesChange(ids);
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, tagIndex) => {
                    const tagProps = getTagProps({ index: tagIndex });
                    return (
                        <Chip
                            {...tagProps}
                            key={`${ruleIndex}-${tagIndex}`}
                            label={option.name}
                            size="small"
                            sx={{ zIndex: 1500 }}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={rule.values.length === 0 ? placeholder : undefined}
                    error={errorText !== null}
                    helperText={errorText}
                />
            )}
        />
    );
}
